import { useState } from 'react';
import { Shield, FlaskConical, Code } from 'lucide-react';
import { WhitepaperForm } from '../components/forms/WhitepaperForm';

const GovernancePage = () => {
  const [showWhitepaperForm, setShowWhitepaperForm] = useState(false);

  return (
    <div data-testid="governance-page">
      {/* Opening */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              SAFE Governance
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              SAFE governance is independent by design.
            </p>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              The entity that defines safety<br />
              is not the entity that builds systems<br />
              and is not the entity that verifies them.
            </p>
          </div>
        </div>
      </section>

      {/* The Failure */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">The Failure</p>
            <h2 className="heading-2 mb-8">
              AI systems are governed by the organisations that build them.
            </h2>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">Developers define safety thresholds.</p>
              <p className="text-lg text-slate-700">Companies assess their own systems.</p>
              <p className="text-lg text-slate-700">Compliance is internally interpreted.</p>
            </div>
            <div className="border-l-4 border-slate-900 pl-6">
              <p className="text-lg text-slate-900 font-medium mb-2">The builder is also the assessor.</p>
              <p className="text-lg text-slate-900 font-semibold">This creates structural conflict.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Rule — standalone */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-2xl md:text-3xl font-semibold tracking-tight">
            No entity can define, implement, and verify its own safety.
          </p>
        </div>
      </section>

      {/* Structural Separation */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mb-12">
            <p className="caption text-cyan-700 mb-4">Structural Separation</p>
            <p className="text-lg text-slate-900 font-semibold">SAFE introduces separation of roles.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mb-8">
            <div className="bg-slate-900 text-white p-8 border-l-4 border-slate-900">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAFE</h3>
              <p className="text-slate-300">Defines what safety is</p>
            </div>
            <div className="bg-cyan-700 text-white p-8 border-l-4 border-cyan-700">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <FlaskConical className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAFE Labs</h3>
              <p className="text-cyan-100">Verifies systems independently</p>
            </div>
            <div className="bg-amber-600 text-white p-8 border-l-4 border-amber-600">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <Code className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAIL</h3>
              <p className="text-amber-100">Implements safety within systems</p>
            </div>
          </div>

          <p className="text-lg text-slate-900 font-semibold max-w-3xl">
            These roles cannot be combined.
          </p>
        </div>
      </section>

      {/* Role of the Council */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Role of the Council</p>
            <h2 className="heading-2 mb-8">
              The SAFE Council defines the SAFE standard.
            </h2>
            <p className="text-lg text-slate-700 mb-6">It is responsible for:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Defining safety thresholds</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Determining harm classifications</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Setting intervention requirements</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Governing updates to the standard</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              The SAFE Council is not advisory. It holds authority over the standard.
            </p>
          </div>
        </div>
      </section>

      {/* Independence */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Independence</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">Council members do not build systems seeking certification.</p>
              <p className="text-lg text-slate-700">They do not verify systems.</p>
              <p className="text-lg text-slate-700">They do not hold commercial influence over outcomes.</p>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              Independence is required to preserve integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Enforcement */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">Enforcement</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-300">Governance without verification cannot enforce safety.</p>
              <p className="text-lg text-slate-300">SAFE governance is inseparable from SAFE Labs certification.</p>
            </div>
            <p className="text-lg text-white font-semibold">
              Without independent verification, governance has no authority.
            </p>
          </div>
        </div>
      </section>

      {/* Whitepaper */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="caption text-cyan-400 mb-4">Governance Whitepaper</p>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
                The complete governance standard in detail.
              </h2>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Structural separation — definition, verification, implementation</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">SAFE-001 risk taxonomy — ten behavioural harm stages (P1–P10)</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Council authority, validation gates, and capture prevention</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Certification tiers — SAFE-Ready, SAFE-Certified, SAFE-Endorsed</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-cyan-400 mt-2.5 flex-shrink-0"></div>
                  <p className="text-sm text-slate-300">Cross-jurisdictional regulatory alignment</p>
                </div>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <button
                onClick={() => setShowWhitepaperForm(true)}
                className="bg-white text-slate-900 px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-100 transition-colors"
                data-testid="btn-request-whitepaper"
              >
                Request Whitepaper
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final Line */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
            If safety is not independently defined and enforced,
          </p>
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mt-2">
            it is not safety.
          </p>
        </div>
      </section>

      <WhitepaperForm
        isOpen={showWhitepaperForm}
        onClose={() => setShowWhitepaperForm(false)}
      />
    </div>
  );
};

export default GovernancePage;
